// src/components/IjrrGear/SingleProduct/ProductCustomizationPanel.js

import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';

export default function ProductCustomizationPanel({
    product,
    selectedColorIndex,
    handleColorClick,
    setPreviewOverlay,
    handleAddOverlayImage,
    onAddToCart,
    userAssets = [],
    onAssetSelect,
    isUserLoggedIn,
    activeAssetUrl,
    onUpload, 
    isUploading,
    // Props for Controlled State
    selectedSize,
    onSizeChange,
    selectedQuantity,
    onQuantityChange,
    // NEW Props for Perspectives
    activeViewUrl,
    onViewChange,
    getProxyUrl
}) {
    // Derived local state for available options
    const [availableSizes, setAvailableSizes] = useState([]);
    const [availableQuantity, setAvailableQuantity] = useState(1);
    
    const [localUploads, setLocalUploads] = useState([]);
    const inventories = product.inventories[selectedColorIndex];

    // --- LOGIC: Build the perspectives list (Front + Additional) ---
    const views = useMemo(() => {
        if (!inventories) return [];
        const front = {
            label: 'Front',
            url: inventories.thumbnail.replace('/thumbs/', '/')
        };
        return [front, ...(inventories.additional_views || [])];
    }, [inventories]);

    // --- LOGIC: Gallery assets ---
    const displayAssets = useMemo(() => {
        const formattedLocals = localUploads.map((url, index) => ({
            id: `local-${index}`,
            url: url,
            isLocal: true
        })).reverse();

        const formattedServer = userAssets.map(asset => ({
            id: asset.id,
            url: asset.url,
            isLocal: false,
            is_main: asset.is_main
        }));

        return [...formattedLocals, ...formattedServer];
    }, [localUploads, userAssets]);

    // --- HANDLERS: File Upload & Asset Selection ---
    const handleFileUpload = (file) => {
        if (file && file.type.startsWith("image/")) {
            const reader = new FileReader();
            reader.onload = (e) => {
                const base64Url = e.target.result;
                setPreviewOverlay(base64Url); 
                handleAddOverlayImage?.(base64Url);

                if (isUserLoggedIn && onUpload) {
                    onUpload(file);
                } else {
                    setLocalUploads(prev => [...prev, base64Url]);
                }
            };
            reader.readAsDataURL(file);
        }
    };

    const handleAssetClick = (asset) => {
        if (onAssetSelect) {
            onAssetSelect(asset);
        } else {
            handleAddOverlayImage?.(asset.url);
            setPreviewOverlay(asset.url);
        }
    };

    // --- SIDE EFFECT: Sizes and Stock ---
    useEffect(() => {
        if (inventories) {
            const sizesArray = Object.entries(inventories.sizes || {});
            setAvailableSizes(sizesArray);

            if (selectedSize) {
                const found = sizesArray.find(([s]) => s === selectedSize);
                if (found) {
                    setAvailableQuantity(found[1]);
                    if (selectedQuantity > found[1]) onQuantityChange(1);
                }
            }
        }
    }, [selectedColorIndex, selectedSize, inventories, selectedQuantity, onQuantityChange]);

    const handleSizeSelect = (size, qty) => {
        onSizeChange(size);
        setAvailableQuantity(qty);
        onQuantityChange(1);
    };

    return (
        <div className="col-md-5 cdx-xl-55 cdxpro-detail">
            <div className="product-detailright">
                <div className="product-card">
                    {/* Breadcrumbs */}
                    <div className="product-breadcrumb d-none d-xl-block mb-3">
                        <Link to="/ijrr-gear" className="breadcrumb-all">All</Link>
                        <span className="breadcrumb-separator">/</span>
                        <span className="breadcrumb-category text-muted">{product.categoryName}</span>
                    </div>

                    {/* Title and Price */}
                    <div className="detail-group">
                        <h2>{product.name}</h2>
                        <ul className="product-price">
                            <li className="new-price">${product.price}</li>
                        </ul>
                    </div>

                    {/* Description */}
                    {product.description?.trim() && (
                        <div className="detail-group">
                            <p className="product-subtitle">{product.description}</p>
                        </div>
                    )}

                    {/* NEW SECTION: Select Perspective */}
                    {views.length > 1 && (
                        <div className="detail-group">
                            <h6>Select Perspective</h6>
                            <div className="d-flex flex-wrap gap-2 mt-2">
                                {views.map((view, idx) => {
                                    const isSelected = activeViewUrl === getProxyUrl(view.url);
                                    return (
                                        <button
                                            key={idx}
                                            type="button"
                                            className={`btn btn-sm ${isSelected ? 'btn-dark' : 'btn-outline-dark'}`}
                                            onClick={() => onViewChange(view.url)}
                                            style={{ borderRadius: '20px', padding: '5px 15px', fontWeight: '600' }}
                                        >
                                            {view.label}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {/* Colors */}
                    <div className="detail-group">
                        <ul className="product-color">
                            {product.inventories.map((variant, index) => (
                                <li
                                    key={index}
                                    className={index === selectedColorIndex ? 'active' : ''}
                                    onClick={() => handleColorClick(index)}
                                    style={{ cursor: 'pointer' }}
                                    data-color={variant.color}
                                >
                                    <div className="product-color-circle" style={{ backgroundImage: `url(${variant.thumbnail})` }}></div>
                                </li>
                            ))}
                        </ul>
                        <small className="text-muted">{product.inventories[selectedColorIndex]?.color}</small>
                    </div>

                    {/* Sizes */}
                    <div className="detail-group">
                        <ul className="product-size">
                            {availableSizes.map(([size, qty], idx) => (
                                <li
                                    key={idx}
                                    className={selectedSize === size ? 'active' : ''}
                                    onClick={() => handleSizeSelect(size, qty)}
                                    style={{ cursor: 'pointer' }}
                                >
                                    {size.toUpperCase()}
                                </li>
                            ))}
                        </ul>
                    </div>

                    {/* Quantity Selector */}
                    <div className="detail-group">
                        <h6>Quantity</h6>
                        <select 
                            className="form-control" 
                            value={selectedQuantity} 
                            onChange={e => onQuantityChange(parseInt(e.target.value, 10))}
                        >
                            {Array.from({ length: availableQuantity }, (_, i) => (
                                <option key={i + 1} value={i + 1}>{i + 1}</option>
                            ))}
                        </select>
                        <small className="text-muted">{availableQuantity} items in stock</small>
                    </div>

                    {/* Customization (Uploader & Gallery) - COMPLETELY RESTORED */}
                    {product.is_customizable && (
                        <div className="detail-group">
                            <h6>Customize it</h6>
                            <div className="product-upload-area" onDrop={e => { e.preventDefault(); handleFileUpload(e.dataTransfer.files[0]); }} onDragOver={e => e.preventDefault()} style={{ position: 'relative', overflow: 'hidden' }}>
                                {isUploading && (
                                    <div style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', background: 'rgba(255,255,255,0.8)', zIndex: 10, display: 'flex', justifyContent: 'center', alignItems: 'center', flexDirection: 'column' }}>
                                        <i className="fa fa-spinner fa-spin" style={{ fontSize: '24px', color: '#c5900b' }}></i>
                                        <span style={{ marginTop: '10px', fontSize: '14px', fontWeight: 'bold' }}>Uploading...</span>
                                    </div>
                                )}
                                <input type="file" id="productFileInput" accept="image/*" onChange={e => handleFileUpload(e.target.files[0])} style={{ display: "none" }} />
                                <div className="upload-content-row">
                                    <button type="button" className="btn btn-primary" onClick={() => document.getElementById("productFileInput").click()} disabled={isUploading}>Upload New</button>
                                    <div className="upload-info-col"><p className="mb-0">Drag & Drop</p><small>JPG, PNG, SVG (max 5MB)</small></div>
                                </div>
                                {displayAssets.length > 0 && (
                                    <div className="mt-3 pt-3 border-top" style={{ width: '100%' }}>
                                        <div className="sidebar-gallery-grid">
                                            {displayAssets.map((asset) => {
                                                const isActive = activeAssetUrl && activeAssetUrl.includes(encodeURIComponent(asset.url));
                                                const isStrictActive = activeAssetUrl === asset.url;
                                                const isMain = asset.is_main;
                                                return (
                                                    <img
                                                        key={asset.id}
                                                        src={asset.url}
                                                        alt="Asset"
                                                        className={`sidebar-gallery-thumb ${(isActive || isStrictActive || (!activeAssetUrl && isMain)) ? 'active-asset' : ''}`}
                                                        onClick={() => handleAssetClick(asset)}
                                                        title={isMain ? "Main Image" : "Click to use"}
                                                    />
                                                );
                                            })}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Add to Cart Button */}
                    <div className="detail-group">
                        <button className="btn btn-primary" onClick={onAddToCart}>Add to cart</button>
                    </div>
                </div>
            </div>
        </div>
    );
}